import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Content, Label, PageHeader, Button, AlertBar, Spinner, Input, TextAreaField } from 'scorer-ui-kit';
import styled from 'styled-components';
import { BoxContainer } from '../components/BoxContainer';
import Popup from 'reactjs-popup';
import { PageHeaderContainer, DeviceNameLabel, CenterAlignedContainer, LeftButton, FieldContainer, SpinnerDesign, PopupTitle, CloseIcon, RightAlignedContainer, NoteLabel } from '../Style';
import { updateDevice, importSettings, exportSettings } from '../apis';
import { useDevice } from '../hooks/useDevice';
import { useLocation } from 'react-router-dom';

const DeviceSettingsHeader = styled(PageHeaderContainer)`
  margin-bottom: 0;  
`;

const FormContainer = styled.div<{ smallScreen: boolean }>`
  display: flex;
  flex-flow: ${({ smallScreen }) => smallScreen ? 'column' : 'row'};;
`;

const RightContainer = styled(RightAlignedContainer)`
  margin-bottom: 30px;
`;

const AlertContainer = styled.div`
  margin-top: 15px;
  margin-bottom: 30px;
  max-width: 790px !important;
`;

const PopupAlertContainer = styled.div`{
  width: auto;
  margin-top: 15px;
}`;

const PopupFieldContainer = styled(FieldContainer)`
  margin-bottom: 36px;
`;

const TextArea = styled(TextAreaField)`
  min-height: 75px;
  resize: vertical;
`;

const PopupContainer = styled.div`
  padding: 30px;
  width: 600px;
`;

const Fieldset = styled(BoxContainer)`
  width: 100%;
  margin-bottom: 20px;
  padding: 20px 15px 15px;
  & > label:last-child {
    margin-bottom: 0;
  }
`;

const FileInput = styled(Input)`
  margin-right: 15px;
  width: 437px;
`;

const overlayStyle = {
  background: 'rgba(0,0,0,0.5)'
};

const FieldsetContainer = styled.div`
  width: 508px;
`;

const BrowseButton = styled(Button)`
  width: 100px;
`;

interface Device {
  name: string,
  detail: string,
  interval: number,
  capture_fps: number,
  roi_lable_name: string,
  roi_lable_note1: string,
  roi_lable_note2: string,
  ng_to_ok_threshold: number,
  ok_to_ng_threshold: number,
}

function DeviceSettings() {
  const [inputs, setInputs] = useState<Device>({ name: '', detail: '', interval: 0, capture_fps: 0, roi_lable_name: '', roi_lable_note1: '', roi_lable_note2: '', ng_to_ok_threshold: 0, ok_to_ng_threshold: 0  });
  const { state: { device, status_code, deviceLoading }, actions: { fetchDevice } } = useDevice();
  const [alert, setAlert] = useState<{ key?: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [readOnly, setReadOnly] = useState(true);
  const [file, setFile] = useState<any>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const location = useLocation();

  const fileInput = useRef<HTMLInputElement | null>(null);

  const { t } = useTranslation(['DeviceSettings', 'Common']);

  useEffect(() => {
    if (!deviceLoading) {
      if (status_code === '0') {
        setInputs(device);
      } else if (status_code === '2') {
        setAlert({ key: new Date(), message: t('Common:deviceNotFound'), type: 'info' });
      }
    }
  }, [device, status_code, deviceLoading, t]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  useEffect(() => {
    sessionStorage.removeItem('cameraTab');
    sessionStorage.removeItem('notificatioinTab');
    sessionStorage.removeItem('auditLogTab');
  }, [location]);

  const handleNumberOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    let regEx = /^([1-9]|[0-9][0-9])*$/;
    let regExNumberBetween1to100 = /\b([1-9]|[1-9][0-9]|100)\b/;

    if(event.target.name==='ok_to_ng_threshold' || 
    event.target.name==='ng_to_ok_threshold'){
      if (regExNumberBetween1to100.exec(event.target.value)) {
        setInputs({ ...inputs, [event.target.name]: parseInt(event.target.value) });
      } else {
        if (event.target.value === '') {
          setInputs({ ...inputs, [event.target.name]: '' });
        }
      }
    } else {
      if (regEx.exec(event.target.value)) {
        if(event.target.name==='capture_fps' || 
        event.target.name==='interval'){
          setInputs(prev => {
            if(event.target.value.length > 5) {
              return ({ ...prev });
            } else {
              return ({ ...prev, [event.target.name]: parseInt(event.target.value) });
            }
          });
        } else {

          setInputs({ ...inputs, [event.target.name]: parseInt(event.target.value) });
        }
      } else {
        if (event.target.value === '') {
          setInputs({ ...inputs, [event.target.name]: '' });
        }
      }
    }
  };
  const onInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputs({ ...inputs, [event.target.name]: event.target.value });
  };

  const onEdit = () => {
    setReadOnly(false);
  };

  const onSave = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    if (!inputs.name.trim().length) {
      setAlert({ key: new Date(), message: t('nameFieldIsRequired'), type: 'error' });
      return;
    }
    if (!inputs.detail.trim().length) {
      setAlert({ key: new Date(), message: t('noteIsRequired'), type: 'error' });
      return;
    }
    if (!Number.isInteger(inputs.capture_fps)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidFPS'), type: 'error' });
      return;
    }
    if(inputs.capture_fps <= 0){
      setAlert({ key: new Date(), message: t('fpsShouldBeGreaterThanZero'), type: 'error' });
      return;
    }
    if (!Number.isInteger(inputs.interval)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidJobIntervalFPS'), type: 'error' });
      return;
    }
    if(inputs.interval <= 0){
      setAlert({ key: new Date(), message: t('jobIntervalShouldBeGreaterThanZero'), type: 'error' });
      return;
    }
    if (!Number.isInteger(inputs.ok_to_ng_threshold)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidOkToNgThreshold'), type: 'error' });
      return;
    }
    if(inputs.ok_to_ng_threshold <= 0){
      setAlert({ key: new Date(), message: t('okToNgShouldBeGreaterThanZero'), type: 'error' });
      return;
    }
    if (!Number.isInteger(inputs.ng_to_ok_threshold)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidNgToOkThreshold'), type: 'error' });
      return;
    }
    if(inputs.ng_to_ok_threshold <= 0){
      setAlert({ key: new Date(), message: t('ngToOkShouldBeGreaterThanZero'), type: 'error' });
      return;
    }
    if (!inputs.roi_lable_name.trim().length) {
      setAlert({ key: new Date(), message: t('nameLabelIsRequired'), type: 'error' });
      return;
    }
    if (!inputs.roi_lable_note1.trim().length) {
      setAlert({ key: new Date(), message: t('note1LabelIsRequired'), type: 'error' });
      return;
    }
    if (!inputs.roi_lable_note2.trim().length) {
      setAlert({ key: new Date(), message: t('note2LabelIsRequired'), type: 'error' });
      return;
    }
    const jsonData = { action: 'set_setting', data: inputs };
    updateDevice(jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          setAlert({ key: new Date(), message: t('settingsUpdatedSuccessfully'), type: 'success' });
          setReadOnly(true);
          fetchDevice();
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
          setReadOnly(false);
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        } else {
          console.error(err);
        }
      });
  };

  const onCancel = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    fetchDevice();
    setReadOnly(true);
  };

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null) {
      setFile(event.target.files[0]);
    }
  };

  const onImportFile = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    if (file.size > 0) {
      setModalOpen(false);
      let formData = new FormData();
      formData.append('file', file);
      importSettings(formData)
        .then((res) => {
          if (res.data.status_code === '0') {
            fetchDevice();
            setAlert({ key: new Date(), message: t('settingsImportedSuccessfully'), type: 'success' });
          } else {
            setAlert({ key: new Date(), message: res.data.message, type: 'error' });
          }
        })
        .catch((err) => {
          if (err.response.data.message) {
            setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
          } else {
            console.error(err);
          }
        });
    } else {
      setAlert({ key: new Date(), message: t('pleaseSelectCSVFile'), type: 'error' });
    }
  };

  const onBrowse = () => {
    if (fileInput.current !== null)
      fileInput.current.click();
  };

  const openModal = () => {
    setFile({});
    setAlert({ key: new Date(), message: '', type: undefined });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setAlert({ key: new Date(), message: '', type: undefined });
  };

  const onExportSettings = () => {
    exportSettings()
      .then((res) => {
        let name = res.headers['content-disposition'];
        name = name.substring(
          name.lastIndexOf(';'),
          name.lastIndexOf('filename=')
        ).split('filename=')[1];

        let fileName = '';
        if (name.length > 0) {
          fileName = name;
        } else {
          fileName = 'LEDMonitor_export_Settings_template.csv';
        }
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        }
      });
  };

  return (
    <Content>
      <DeviceSettingsHeader>
        <PageHeader title={t('deviceSettings')} icon='ViewSettings' areaTitle={t('Common:home')} areaHref='/' />
        <DeviceNameLabel htmlFor='' labelText={device.name} />
        <NoteLabel htmlFor='' labelText={device.detail} />
      </DeviceSettingsHeader>
      {
        deviceLoading &&
          <CenterAlignedContainer>
            <Spinner size='large' styling={SpinnerDesign} />
          </CenterAlignedContainer>
          
      }
      {
        !deviceLoading && inputs &&
          <>
            <RightContainer>
              <Popup
                modal
                open={modalOpen}
                onClose={closeModal}
                {...{ overlayStyle }}
                closeOnDocumentClick
              >
                <PopupContainer>
                  <CloseIcon onClick={closeModal}>x</CloseIcon>
                  <CenterAlignedContainer>
                    <PopupTitle>{t('selectCSVFile')}</PopupTitle>
                  </CenterAlignedContainer>
                  <PopupFieldContainer>
                    <input type='file' ref={fileInput} onChange={onChangeFile} accept='.csv' style={{ display: 'none' }} />
                    {file && <FileInput value={file.name} placeholder={t('noFileSelected')} readOnly />}
                    <BrowseButton onClick={onBrowse}>{t('browse')}</BrowseButton>
                  </PopupFieldContainer>
                  <CenterAlignedContainer>
                    <LeftButton design='secondary' onClick={closeModal}>{t('Common:cancel')}</LeftButton>
                    <Button onClick={onImportFile}>{t('import')}</Button>
                  </CenterAlignedContainer>
                  {
                    (alert.message !== '' && alert.type === 'error' &&
                      <PopupAlertContainer key={alert.key}>
                        <AlertBar type={alert.type} message={alert.message} />
                      </PopupAlertContainer>
                    )
                  }
                </PopupContainer>
              </Popup>
              <LeftButton design='secondary' onClick={openModal}>{t('importSettings')}</LeftButton>
              <Button design='secondary' onClick={onExportSettings}>{t('exportSettings')}</Button>
            </RightContainer>

            {
              status_code === '0' && <>
                <FormContainer smallScreen={window.innerWidth < 1600}>
                  <FieldsetContainer>
                    <Fieldset title={t('general')}>
                      <Label htmlFor='name' labelText={t('deviceName')}>
                        <Input name='name' maxLength={255} value={inputs.name} placeholder={t('deviceName')} onChange={onInputChange} disabled={readOnly} required/>
                      </Label>
                      <TextArea label={t('description')} fieldState='default' name='detail' maxLength={255} value={inputs.detail} placeholder={t('description')} onChange={onInputChange} disabled={readOnly} required/>
                    </Fieldset>
                    <Fieldset title={t('analysis')}>
                      <Label htmlFor='capture_fps' labelText={t('videoFPS')}>
                        <Input name='capture_fps' type='number' value={inputs.capture_fps} placeholder={t('videoFPS')} onChange={handleNumberOnChange} disabled={readOnly} required/>
                      </Label>
                      <Label htmlFor='interval' labelText={t('analysisJobInterval')}>
                        <Input name='interval' type='number' value={inputs.interval} placeholder={t('analysisJobInterval')} onChange={handleNumberOnChange} disabled={readOnly} required/>
                      </Label>
                      <Label htmlFor='ok_to_ng_threshold' labelText={t('okToNgThreshold')}>
                        <Input name='ok_to_ng_threshold' type='number' min={1} max={100} value={inputs.ok_to_ng_threshold} placeholder={t('okToNgThreshold')} onChange={handleNumberOnChange} disabled={readOnly} required/>
                      </Label>
                      <Label htmlFor='ng_to_ok_threshold' labelText={t('ngToOkThreshold')}>
                        <Input name='ng_to_ok_threshold' type='number' min={1} max={100} value={inputs.ng_to_ok_threshold} placeholder={t('ngToOkThreshold')} onChange={handleNumberOnChange} disabled={readOnly} required/>
                      </Label>
                    </Fieldset>
                    <Fieldset title={t('labelsForDetectionAnalysis')}>
                      <Label htmlFor='roi_lable_name' labelText={t('name')} >
                        <Input name='roi_lable_name' value={inputs.roi_lable_name} maxLength={64} placeholder={t('namePlaceholder')} onChange={onInputChange} disabled={readOnly} required/>
                      </Label>
                      <Label htmlFor='roi_lable_note1' labelText={t('note1')} >
                        <Input name='roi_lable_note1' value={inputs.roi_lable_note1} maxLength={64} placeholder={t('note1Placeholder')} onChange={onInputChange} disabled={readOnly} required/>
                      </Label>
                      <Label htmlFor='roi_lable_note2' labelText={t('note2')}>
                        <Input name='roi_lable_note2' value={inputs.roi_lable_note2} maxLength={64} placeholder={t('note2Placeholder')} onChange={onInputChange} disabled={readOnly} required/>
                      </Label>
                    </Fieldset>
                    <RightContainer>
                      {readOnly ?
                        <Button design='primary' onClick={onEdit}>{t('Common:edit')}</Button> :
                        <>
                          <LeftButton design='secondary' onClick={onCancel}>{t('Common:cancel')}</LeftButton>
                          <Button design='primary' onClick={onSave}>{t('Common:save')}</Button>
                        </>}

                    </RightContainer>
                  {
                    (alert.message !== '' && !modalOpen &&
                      <AlertContainer key={alert.key}>
                        <AlertBar type={alert.type} message={alert.message} />
                      </AlertContainer>
                    )
                  }
                  </FieldsetContainer>
                </FormContainer>
              </>
            }
          </>
      }
    </Content>
  );
}

export default DeviceSettings;
